import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandLogo } from "../brand/BrandLogo";

/* ─────────────────────────────────────────────────────────────
   MarketingFooter · Phase 5 Homepage
   ------------------------------------------------------------
   White footer. Only genuine destinations linked:
     Product   → in-page anchors (real destinations on this page)
     Account   → /login, /signup
   No Privacy / Terms group — those pages do not exist in the
   app routes today.  Reported as missing in the Phase 5 wrap-up
   rather than published as fake links.
   No fake address, no fake registration, no social icons, no
   compliance badges, no newsletter form.
   ───────────────────────────────────────────────────────────── */

const NAV_GROUPS = [
  {
    label: "Product",
    links: [
      { label: "Product",       href: "#product" },
      { label: "How it works",  href: "#how-it-works" },
      { label: "For agencies",  href: "#for-agencies" },
      { label: "Referrals",     href: "#referrals" },
      { label: "Health check",  href: "#pipeline-health-check" },
    ],
  },
];

export const MarketingFooter = () => {
  const nav = useNavigate();
  const year = new Date().getFullYear();
  return (
    <footer
      className="relative"
      style={{ background: "#FFFFFF", borderTop: "1px solid rgba(15,23,42,0.06)" }}
      data-testid="landing-footer"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-14 lg:pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-10 lg:gap-14">
          {/* Brand + descriptor */}
          <div>
            <Link to="/" data-testid="landing-footer-brand" className="inline-block">
              <BrandLogo tone="dark" iconSize={28} wordSize={20} />
            </Link>
            <p className="text-[13px] text-slate-600 leading-relaxed mt-4 max-w-[360px]">
              The operating system for property transactions.
            </p>
          </div>

          {/* Product group */}
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-slate-500">
              {NAV_GROUPS[0].label}
            </p>
            <ul className="mt-4 space-y-2.5">
              {NAV_GROUPS[0].links.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-[13px] font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                    data-testid={`landing-footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Account group */}
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-slate-500">Account</p>
            <ul className="mt-4 space-y-2.5">
              <li>
                <button
                  type="button"
                  onClick={() => nav("/login")}
                  className="text-[13px] font-semibold text-slate-700 hover:text-slate-900"
                  data-testid="landing-footer-signin"
                >
                  Sign in
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => nav("/signup")}
                  className="text-[13px] font-semibold text-slate-700 hover:text-slate-900"
                  data-testid="landing-footer-request-access"
                >
                  Request access
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-[11.5px] text-slate-500">© {year} ChainFlow. All rights reserved.</p>
          <p className="text-[11.5px] text-slate-400">Made for UK estate agencies.</p>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
